import React, { useState, useMemo, useEffect } from 'react'
import { Button, Row, Col, Card, Pagination, Tag, Modal, Form, Input, Select, message } from 'antd'
import { BackgroundContainer } from '../../atom/layout/backgroundContainer'
import { Box } from '../../atom/box'
import { withAuthenticatedLayout } from '../../components/layouts/layout'
import { theme } from '../../theme'
import { styled } from '../../theme'
import { useAppState } from '../../state'
import { getFullNameFromEmail } from '../../helpers/users'
import { GetTenants } from '../../services/data-provider/tenants'
import { TenantFilter } from '../../models/filter'
import { tenantStatusesDefault } from '../../models/tenant'
import {
  GetMarketplaceIntegrations,
  SendMarketplaceIntegrationRequest,
} from '../../services/data-provider/integrations-marketplace'
import IntegrationsSelectors from './components/integrations-selectors'
import { LearnMoreModal } from './components/learn-more-modal'
import { Integration } from './types'

const IntegrationCard = styled(Card)`
  height: 100%;
  margin-bottom: 24px;
  border-radius: 4px;
  box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0px 4px 17px rgba(0, 0, 0, 0.12);
  }

  .ant-card-body {
    padding-bottom: 0px;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .integration-header {
    display: flex;
    gap: 0;
    align-items: center;
  }

  .integration-logo-container {
    flex: 0.6;
    order: 2;
    min-width: 0;
  }

  .integration-logo {
    width: 100%;
    height: 120px;
    object-fit: contain;
    background: white;
    border-radius: 8px;
    padding: 12px;
    padding-right: 0px;
    box-sizing: border-box;
    max-width: 100%;
    max-height: 100%;
  }

  .integration-header-content {
    display: flex;
    flex-direction: column;
    flex: 4;
    min-width: 0;
    order: 1;
    flex-grow: 1;
  }

  .integration-categories {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 6px;
  }

  .integration-categories .ant-tag {
    font-size: 10px;
    padding: 3px 10px;
    margin: 0;
    line-height: 1.2;
  }

  .integration-name {
    font-size: 16px;
    font-weight: 600;
    color: ${(props) => props.theme.colors.softBlack};
    margin-bottom: 8px;
  }

  .integration-description {
    color: ${(props) => props.theme.colors.softBlack};
    font-size: 12px;
    line-height: 1.5;
    margin-bottom: 0;
    flex-grow: 1;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 36px; /* 2 lines * 18px line-height */
  }

  .integration-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    width: 100%;
  }

  .integration-actions button,
  .integration-actions .ant-btn {
    flex: 1;
    min-width: 0;
    font-size: 12px;
    height: 31px;
  }
`

const ComingSoonCard = styled(Card)`
  height: 100%;
  margin-bottom: 24px;
  border-radius: 4px;
  border: 2px dashed ${(props) => props.theme.colors.grey6 || '#d9d9d9'};
  box-shadow: none;
  background: ${(props) => props.theme.colors.grayBackground || '#fafafa'};
  cursor: default;
  
  .ant-card-body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    align-items: center;
    text-align: center;
  }
`


const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;

  .ant-pagination {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
  }

  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next,
  .ant-pagination-jump-prev,
  .ant-pagination-jump-next {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .ant-pagination-item a {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .ant-pagination-next::after {
    transform: rotate(180deg);
  }
`


const IntegrationsMarketplaceBasePage: React.FC = () => {
  document.querySelector('body')?.classList.add('redesignActive')

  // Inject global styles for modal (modals render in portal)
  React.useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .request-integration-modal .ant-select-selection-overflow {
        margin-left: 0 !important;
      }
      .integrations-category-selector .ant-select-clear,
      .integrations-property-type-selector .ant-select-clear {
        right: 50px !important;
      }
      .integrations-category-selector .ant-select-multiple.ant-select-allow-clear .ant-select-selector,
      .integrations-property-type-selector .ant-select-multiple.ant-select-allow-clear .ant-select-selector {
        padding-right: 50px !important;
      }
    `
    document.head.appendChild(style)
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style)
      }
    }
  }, [])

  const { currentUser, isMockUpEnabled } = useAppState()
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loadingIntegrations, setLoadingIntegrations] = useState(false)
  const [selectedIntegrations, setSelectedIntegrations] = useState<string[]>([]) // Applied integration names
  const [selectedIntegrationsTemp, setSelectedIntegrationsTemp] = useState<string[]>([]) // Temporary integration names
  const [searchTextValue, setSearchTextValue] = useState('') // Temporary value for input
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]) // Applied categories
  const [selectedCategoriesTemp, setSelectedCategoriesTemp] = useState<string[]>([]) // Temporary categories
  const [selectedPropertyTypes, setSelectedPropertyTypes] = useState<string[]>([]) // Applied property types
  const [selectedPropertyTypesTemp, setSelectedPropertyTypesTemp] = useState<string[]>([]) // Temporary property types
  const [currentPage, setCurrentPage] = useState(1)
  const [requestModalVisible, setRequestModalVisible] = useState(false)
  const [learnMoreModalVisible, setLearnMoreModalVisible] = useState(false)
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null)
  const [additionalNotes, setAdditionalNotes] = useState('')
  const [siteOptions, setSiteOptions] = useState<string[]>([])
  const [loadingSites, setLoadingSites] = useState(false)
  const [sitesDisabled, setSitesDisabled] = useState(false)
  const [sitesDisabledMessage, setSitesDisabledMessage] = useState('')

  const pageSize = 12

  // Fixed filter lists
  const categoryOptions = ['Proptech', 'Energy Management', 'Parking', 'Car Sharing', 'Utilities', 'Utilization']

  const propertyTypeOptions = ['Fleet', 'Hospitality', 'Commercial', 'Single Family Homes', 'Multifamily', 'Parking Facilities']

  // Fetch integrations from API
  useEffect(() => {
    const fetchIntegrations = async () => {
      setLoadingIntegrations(true)
      try {
        const data = await GetMarketplaceIntegrations(isMockUpEnabled)
        setIntegrations(Array.isArray(data) ? data : [])
      } catch (error) {
        setIntegrations([])
      } finally {
        setLoadingIntegrations(false)
      }
    }

    fetchIntegrations()
  }, [isMockUpEnabled])

  // Filter integrations based on search and filters
  const filteredIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      // Filter by selected integration names
      if (selectedIntegrations.length > 0 && !selectedIntegrations.includes(integration.name)) {
        return false
      }

      // Filter by category
      if (selectedCategories.length > 0) {
        const integrationCategories = integration.category.split(',').map((c) => c.trim())
        const hasMatchingCategory = selectedCategories.some((selected) =>
          integrationCategories.some((cat) => cat === selected),
        )
        if (!hasMatchingCategory) {
          return false
        }
      }

      // Filter by property type
      if (selectedPropertyTypes.length > 0) {
        const integrationPropertyTypes = integration.property_type.split(',').map((p) => p.trim())
        const hasMatchingPropertyType = selectedPropertyTypes.some((selected) =>
          integrationPropertyTypes.includes(selected),
        )
        if (!hasMatchingPropertyType) {
          return false
        }
      }

      return true
    })
  }, [integrations, selectedIntegrations, selectedCategories, selectedPropertyTypes])

  // Paginate filtered results
  const paginatedIntegrations = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    return filteredIntegrations.slice(startIndex, startIndex + pageSize)
  }, [filteredIntegrations, currentPage])

  const totalPages = Math.ceil(filteredIntegrations.length / pageSize)
  
  // Check if we should show "Coming Soon" card
  // Only show when no filters/search are active
  // If last page is full (9 cards), show on a new page, otherwise show on last page
  const hasNoFilters = selectedIntegrations.length === 0 && selectedCategories.length === 0 && selectedPropertyTypes.length === 0
  const lastPageIsFull = filteredIntegrations.length > 0 && filteredIntegrations.length % pageSize === 0
  const shouldShowComingSoon = 
    hasNoFilters && 
    ((lastPageIsFull && currentPage === totalPages + 1) || (!lastPageIsFull && currentPage === totalPages))
  
  // Adjust total pages to include "Coming Soon" page if needed
  const displayTotalPages = hasNoFilters && lastPageIsFull ? totalPages + 1 : totalPages

  const handleCategoryChange = (values: string[]) => {
    setSelectedCategoriesTemp(values)
  }

  const handlePropertyTypeChange = (values: string[]) => {
    setSelectedPropertyTypesTemp(values)
  }

  const handleSearch = () => {
    setSelectedIntegrations(selectedIntegrationsTemp)
    setSelectedCategories(selectedCategoriesTemp)
    setSelectedPropertyTypes(selectedPropertyTypesTemp)
    setCurrentPage(1) // Reset to first page on search
  }

  const handleIntegrationChange = (values: string[]) => {
    setSelectedIntegrationsTemp(values)
  }

  const handleLearnMore = (integration: Integration) => {
    setSelectedIntegration(integration)
    setLearnMoreModalVisible(true)
  }

  const handleCloseLearnMore = () => {
    setLearnMoreModalVisible(false)
    setSelectedIntegration(null)
  }

  const handleRequest = (integration: Integration | null = null) => {
    setSelectedIntegration(integration)
    setRequestModalVisible(true)
    form.resetFields()
    setAdditionalNotes('')
  }

  const handleCancelRequest = () => {
    setRequestModalVisible(false)
    setSelectedIntegration(null)
    form.resetFields()
    setAdditionalNotes('')
    form.setFieldsValue({
      email: userEmail,
      name: userName,
      site: [],
      additionalNotes: '',
    })
  }

  const handleSubmitRequest = async () => {
    try {
      setSubmitting(true)
      const values = await form.validateFields()
      
      // Ensure email and name are not empty
      const email = (values.email || '').trim()
      const name = (values.name || '').trim()
      
      if (!email) {
        message.error('Email is required')
        setSubmitting(false)
        return
      }
      
      if (!name) {
        message.error('Name is required')
        setSubmitting(false)
        return
      }
      
      const sitesString = values.site && values.site.length > 0 ? values.site.join(',') : ''
      
      const result = await SendMarketplaceIntegrationRequest(
        {
          integrationName: selectedIntegration?.name || 'General Inquiry',
          email: email,
          name: name,
          sites: sitesString,
          additionalNotes: values.additionalNotes || '',
        },
        isMockUpEnabled,
      )
      
      if (result.success) {
        message.success('Your integration request has been submitted successfully')
        handleCancelRequest()
      } else {
        message.error(result.message || 'Failed to submit request. Please try again.')
      }
    } catch (error) {
      message.error('Failed to submit request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // Fetch sites from API
  useEffect(() => {
    const fetchSites = async () => {
      setLoadingSites(true)
      setSitesDisabled(false)
      setSitesDisabledMessage('')
      try {
        const filter: TenantFilter = {
          page: 1,
          status: tenantStatusesDefault,
          masterAccounts: [],
          country: [],
          province: [],
        }
        
        // First, fetch page 1 to check total count
        const firstPageResponse = await GetTenants(isMockUpEnabled, filter)
        const totalCount = firstPageResponse.pagination.totalEntries
        
        // If total count exceeds 60, disable the input
        if (totalCount > 60) {
          setSitesDisabled(true)
          setSitesDisabledMessage(
            'Site selection is not needed for accounts with more than 60 sites.'
          )
          setSiteOptions([])
          setLoadingSites(false)
          return
        }
        
        // Load exactly 4 pages (4 API calls total: page 1 already fetched, then pages 2-4)
        // Each page has 20 sites = max 80 sites
        const allTenants: string[] = []
        const perPage = firstPageResponse.pagination.perPage || 20
        
        // Add page 1 results
        const page1TenantNames = firstPageResponse.data.map((tenant) => tenant.name)
        allTenants.push(...page1TenantNames)
        
        // If page 1 has less than perPage items, we're done (no more pages)
        if (firstPageResponse.data.length < perPage) {
          setSiteOptions(allTenants)
          setLoadingSites(false)
          return
        }
        
        // Fetch pages 2, 3, 4 (3 more API calls)
        for (let page = 2; page <= 4; page++) {
          const pageFilter: TenantFilter = {
            ...filter,
            page: page,
          }
          const response = await GetTenants(isMockUpEnabled, pageFilter)
          const pageTenantNames = response.data.map((tenant) => tenant.name)
          allTenants.push(...pageTenantNames)
          
          // If this page has less than perPage items, we've reached the last page
          if (response.data.length < perPage) {
            break
          }
          
          // If we've loaded all available tenants, break early
          if (allTenants.length >= totalCount) {
            break
          }
        }
        
        setSiteOptions(allTenants)
      } catch (error) {
        setSiteOptions([])
      } finally {
        setLoadingSites(false)
      }
    }

    if (requestModalVisible) {
      fetchSites()
    }
  }, [requestModalVisible, isMockUpEnabled])

  // Get user information
  const userEmail = currentUser?.email || ''
  const userDisplayName = currentUser?.name || currentUser?.email || ''
  const [firstName, lastName] = getFullNameFromEmail(userDisplayName)
  const userName = firstName && lastName ? `${firstName} ${lastName}` : userDisplayName

  return (
    <BackgroundContainer>
        <Box padding style={{ backgroundColor: "transparent" }}>
        <div>
          <p style={{ color: theme.colors.grey6 || theme.colors.gray || '#666', marginBottom: '10px' }}>
            SWTCH Marketplace is your hub for discovering trusted integrations that enhance your EV charging program.
            <br />
            Browse vetted partners, see how they work with SWTCH OS, and quickly connect with our team to activate the
            solutions that fit your property.
          </p>

          {/* Search and Filter Row */}
          <Row gutter={[16, 16]} align="middle" style={{ marginBottom: '24px' }}>
            <Col flex="auto">
              <IntegrationsSelectors
                loading={loadingIntegrations}
                onIntegrationChange={handleIntegrationChange}
                selectedIntegrations={selectedIntegrationsTemp}
                selectedCategories={selectedCategoriesTemp}
                onCategoryChange={handleCategoryChange}
                selectedPropertyTypes={selectedPropertyTypesTemp}
                onPropertyTypeChange={handlePropertyTypeChange}
                categoryOptions={categoryOptions}
                propertyTypeOptions={propertyTypeOptions}
                onSearch={handleSearch}
                dropdownClassName="not-in-modal-selector"
                integrations={integrations}
              />
            </Col>
            <Col flex="none">
              <Button style={{ height: '31px' }} type="primary" onClick={() => handleRequest(null)}>
                HELP US GROW
              </Button>
            </Col>
          </Row>

          {/* Integrations Grid */}
          {loadingIntegrations ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Loading integrations...</div>
          ) : (paginatedIntegrations.length === 0 && !shouldShowComingSoon) ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>No integrations found</div>
          ) : (
              <Row gutter={[24, 24]}>
                {paginatedIntegrations.map((integration, index) => (
                  <Col xs={24} sm={12} lg={8} key={`${integration.name}-${index}`}>
                    <IntegrationCard>
                      <div className="integration-categories">
                        {integration.category.split(',').map((cat, catIndex) => (
                          <Tag style={{ margin: '0px' }} key={catIndex}>
                            {cat.trim()}
                          </Tag>
                        ))}
                      </div>
                      <div className="integration-header">
                        <div className="integration-header-content">
                          <div className="integration-name">
                            {integration.name}
                          </div>
                          <div className="integration-description">
                            {integration.short_description}
                          </div>
                          <div className="integration-actions">
                            <Button onClick={() => handleLearnMore(integration)}>Learn More</Button>
                            <Button type="primary" onClick={() => handleRequest(integration)}>
                              Request
                            </Button>
                          </div>
                        </div>
                        <div className="integration-logo-container">
                          <img src={integration.logo_url || ''} alt={integration.name} className="integration-logo" />
                        </div>
                      </div>
                    </IntegrationCard>
                  </Col>
                ))}
                {shouldShowComingSoon && (
                  <Col xs={24} sm={12} lg={8}>
                    <ComingSoonCard>
                      <div style={{ padding: '20px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 600, color: theme.colors.softBlack, marginBottom: '12px' }}>
                          More Coming Soon
                        </h3>
                        <p style={{ fontSize: '12px', color: theme.colors.grey6 || '#666', margin: 0, lineHeight: 1.5 }}>
                          We're continuously adding new integrations to enhance your EV charging experience.
                        </p>
                      </div>
                    </ComingSoonCard>
                  </Col>
                )}
              </Row>
          )}

              {/* Pagination */}
          {displayTotalPages > 1 && (
            <PaginationContainer>
              <Pagination
                current={currentPage}
                total={filteredIntegrations.length + (hasNoFilters && lastPageIsFull ? pageSize : 0)}
                pageSize={pageSize}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
                showQuickJumper={false}
              />
            </PaginationContainer>
          )}
        </div>
      </Box>

      {/* Learn More Modal */}
      <LearnMoreModal
        visible={learnMoreModalVisible}
        integration={selectedIntegration}
        onCancel={handleCloseLearnMore}
        onRequest={handleRequest}
      />

      {/* Request Integration Modal */}
      <Modal
          title={selectedIntegration ? "Request Integration" : "Ask About an Integration"}
          visible={requestModalVisible}
          onCancel={handleCancelRequest}
          footer={
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button key="cancel" onClick={handleCancelRequest} disabled={submitting}>
                Cancel
              </Button>
              <Button key="submit" type="primary" onClick={handleSubmitRequest} loading={submitting}>
                Submit Inquiry
              </Button>
            </div>
          }
          width={600}
          wrapClassName="request-integration-modal"
        >
          {selectedIntegration && (
            <div style={{ marginBottom: '10px', fontSize: '14px', color: theme.colors.grey6 }}>
              Submit your inquiry for {selectedIntegration.name}
            </div>
          )}
          <Form
          form={form}
          layout="vertical"
          initialValues={{
            email: userEmail,
            name: userName,
            site: [],
            additionalNotes: '',
          }}
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[{ required: true, type: 'email', message: 'Please enter a valid email address' }]}
            style={{ marginBottom: '10px' }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter your name' }]}
            style={{ marginBottom: '20px' }}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Site"
            name="site"
            rules={[{ required: false, message: 'Please select at least one site' }]}
            style={{ marginBottom: '10px' }}
            help={sitesDisabledMessage ? sitesDisabledMessage : undefined}
          >
            <Select
              mode="multiple"
              placeholder={sitesDisabled ? 'Site selection unavailable' : 'Select sites'}
              loading={loadingSites}
              allowClear
              disabled={sitesDisabled}
              className="request-modal-select"
            >
              {siteOptions.map((site) => (
                <Select.Option key={site} value={site}>
                  {site}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Additional Notes (Optional)"
            name="additionalNotes"
            rules={[{ max: 1000, message: 'Additional notes cannot exceed 1000 characters' }]}
            style={{ marginBottom: '10px' }}
          >
            <Input.TextArea
              rows={4}
              placeholder="Add any additional information or questions..."
              maxLength={1000}
              showCount
              value={additionalNotes}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdditionalNotes(e.target.value)}
            />
          </Form.Item>
        </Form>
        </Modal>
      </BackgroundContainer>
  )
}

export const IntegrationsMarketplacePage = withAuthenticatedLayout(IntegrationsMarketplaceBasePage)
